import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import useReducedMotion from '../hooks/useReducedMotion';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { t, lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) {
      setError(t('auth.duo.reset.passwordTooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.duo.reset.passwordsDontMatch'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(API + '/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success(t('auth.duo.reset.doneTitle'));
    } catch (err) {
      const msg = getApiErrorMessage(err, lang);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center duo-bg px-4 py-10">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center" style={{ border: '2px solid var(--duo-stroke)' }}>
          <p className="text-[var(--duo-error-dark)] font-bold mb-4">{t('auth.duo.reset.invalid')}</p>
          <Link to="/login" className="btn-duo-secondary inline-flex">
            {t('auth.duo.forgot.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center duo-bg px-4 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <motion.div
          className="text-center mb-8"
          initial={reduced ? false : { opacity: 0, y: -10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--duo-green)' }}>
              <span className="text-white font-duo-display text-2xl leading-none">K</span>
            </div>
            <span className="font-duo-display text-2xl text-[var(--duo-text)]">Kariyer Online</span>
          </Link>
        </motion.div>

        <motion.div
          className="bg-white rounded-2xl p-8"
          initial={reduced ? false : { opacity: 0, scale: 0.96 }}
          animate={reduced ? undefined : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          style={{ border: '2px solid var(--duo-stroke)' }}
        >
          {done ? (
            <div className="text-center" data-testid="reset-done-state">
              <motion.div
                className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
                style={{ backgroundColor: 'var(--duo-green)', boxShadow: '0 4px 0 0 var(--duo-green-dark)' }}
                initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                animate={reduced ? undefined : { scale: [0.5, 1.2, 0.95, 1], opacity: 1 }}
                transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.5, 0.8, 1] }}
              >
                <CheckCircle2 size={32} strokeWidth={2.5} className="text-white" />
              </motion.div>
              <h2 className="font-duo-display text-2xl text-[var(--duo-text)] mb-3">
                {t('auth.duo.reset.doneTitle')}
              </h2>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-7">
                {t('auth.duo.reset.doneDesc')}
              </p>
              <button onClick={() => navigate('/login')} className="btn-duo-primary" data-testid="go-to-login-btn">
                {t('auth.duo.reset.goToLogin')}
              </button>
            </div>
          ) : (
            <>
              <h2 className="font-duo-display text-3xl text-[var(--duo-text)] mb-2" data-testid="reset-title">
                {t('auth.duo.reset.title')}
              </h2>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-7">
                {t('auth.duo.reset.subtitle')}
              </p>

              {error && (
                <div className="bg-[#FFF5F5] border-2 border-[var(--duo-error)] text-[var(--duo-error-dark)] px-4 py-2 rounded-xl mb-4 text-sm font-bold" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                    {t('auth.duo.reset.newPassword')}
                  </label>
                  <div className="input-duo-wrap">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={t('auth.duo.reset.placeholderNew')}
                      className="input-duo"
                      required
                      autoFocus
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="input-duo-affix" aria-label="Toggle password visibility">
                      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                    {t('auth.duo.reset.confirmPassword')}
                  </label>
                  <div className="input-duo-wrap">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder={t('auth.duo.reset.placeholderConfirm')}
                      className={'input-duo' + (confirm && confirm !== password ? ' has-error' : '')}
                      required
                      data-testid="confirm-password-input"
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="input-duo-affix" aria-label="Toggle password visibility">
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {confirm && confirm !== password && (
                    <p className="text-xs text-[var(--duo-error-dark)] mt-2 font-bold">
                      {t('auth.duo.reset.passwordsDontMatch')}
                    </p>
                  )}
                </div>

                <button type="submit" disabled={loading} className="btn-duo-primary w-full mt-4" data-testid="reset-submit-btn">
                  {loading ? t('auth.duo.reset.saving') : t('auth.duo.reset.cta')}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
